import { HeadlessOptions, HeadlessParameters } from './headlessTypes';

/**
 * Explicit playback: HLS and WHEP.
 *
 * **This is deliberately not part of the media-card surface.** WebRTC media is
 * *auto-consumed* — the SDK subscribes for you, and `getRemoteVideoStreams` /
 * `getAudioGridComponents` hand you what already arrived. HLS and WHEP are the
 * opposite: nothing is consumed until you ask, because each one opens a
 * connection the room did not ask for and would otherwise pay for.
 *
 * So the API here is imperative — `attachPlayback` / `detachPlayback` — rather
 * than a reactive getter. Mixing these into the media cards would mean every
 * room silently opened an HLS or WHEP connection whether or not anything
 * rendered it.
 *
 * When to reach for which:
 *
 * | | latency | cost per viewer | use it for |
 * | --- | --- | --- | --- |
 * | WebRTC (auto) | sub-second | an SFU consumer | anyone who takes part |
 * | WHEP | sub-second | an SFU consumer | standards-based egress to non-SDK players |
 * | HLS | seconds | a CDN request | audiences too large to fan out over the SFU |
 *
 * A large passive audience on HLS is the reason this exists: it moves the
 * expensive part — media fan-out — off the SFU.
 */

export type PlaybackProtocol = 'hls' | 'whep';

export interface PlaybackSource {
  protocol: PlaybackProtocol;
  url: string;
}

/**
 * Playback URLs the server has published for this room.
 *
 * Returns nothing when the room has none — HLS and WHEP are provisioned
 * server-side, so absence means "not enabled for this room", not an error.
 */
export function getPlaybackSources({ parameters }: HeadlessOptions): PlaybackSource[] {
  const source: HeadlessParameters = parameters || {};
  const sources: PlaybackSource[] = [];
  const push = (protocol: PlaybackProtocol, url: unknown) => {
    if (typeof url === 'string' && /^https:\/\//i.test(url)) sources.push({ protocol, url });
  };
  push('hls', source.hlsUrl || source.hlsPlaybackUrl || source.playbackUrl);
  push('whep', source.whepUrl || source.whepPlaybackUrl);
  return sources;
}

/**
 * Every HLS URL a broadcast offers, in the order to try them.
 *
 * A broadcast created through the REST API returns `playbackUrl` (the primary,
 * often a CDN), `playbackUrls` (delivery edges, often empty) and
 * `originPlaybackUrl` (the media server itself). The origin is the fallback that
 * keeps a viewer watching when the CDN or an edge fails, so pass the rest of
 * this list as `fallbackSources` to `attachPlayback`.
 */
export function getHlsPlaybackCandidates(broadcast: {
  playbackUrl?: unknown;
  playbackUrls?: unknown;
  originPlaybackUrl?: unknown;
} | null | undefined): string[] {
  const urls = [
    broadcast?.playbackUrl,
    ...(Array.isArray(broadcast?.playbackUrls) ? broadcast!.playbackUrls as unknown[] : []),
    broadcast?.originPlaybackUrl,
  ];
  return [...new Set(urls.filter((url): url is string => typeof url === 'string' && /^https:\/\//i.test(url)))];
}

/** A running playback. Always `detachPlayback` it — none of this is garbage-collected. */
export interface PlaybackHandle {
  protocol: PlaybackProtocol;
  /** The URL playing now; changes when a fallback takes over. */
  url: string;
  /** The media, when the protocol produces one directly (WHEP). */
  stream: MediaStream | null;
  /** Release the connection, the player, and the element's source. */
  detach: () => void;
}

export interface AttachPlaybackOptions {
  /** Playback URL, or a `PlaybackSource` from `getPlaybackSources`. */
  source: string | PlaybackSource;
  /** The element to play into. Required for HLS; optional for WHEP. */
  video?: HTMLVideoElement | null;
  /** Force a protocol. Inferred from the URL when omitted. */
  protocol?: PlaybackProtocol;
  /**
   * Factory returning an hls.js instance, e.g. `() => new Hls({ lowLatencyMode: true })`.
   *
   * Required for HLS anywhere except Safari, and injected rather than imported
   * so hls.js never becomes a dependency of this SDK — most rooms never play
   * HLS, and React Native cannot use it at all.
   */
  hlsFactory?: () => any;
  /** Extra headers for the WHEP POST, e.g. an auth token. */
  headers?: Record<string, string>;
  /** ICE servers for WHEP. Defaults to the room's own when available. */
  iceServers?: RTCIceServer[];
  /**
   * HLS only: further URLs to try, in order, when the current one fails — for a
   * broadcast, everything after the first entry of `getHlsPlaybackCandidates`.
   *
   * A fatal network error (or, on native HLS, a video error) moves to the next
   * URL. A 401/403 does not: a refused token is final, never bypassed by trying
   * another host. A media error is first given one `recoverMediaError()`.
   */
  fallbackSources?: Array<string | PlaybackSource>;
  /** HLS only: called after a fallback takes over, with the new URL and its position. */
  onSourceChange?: (url: string, index: number) => void;
  /** HLS only: called once when playback cannot continue (every URL failed, or access was refused). */
  onPlaybackError?: (error: Error) => void;
}

interface HlsFailover {
  fallbackUrls: string[];
  onSourceChange?: (url: string, index: number) => void;
  onPlaybackError?: (error: Error) => void;
}

function inferProtocol(url: string, explicit?: PlaybackProtocol): PlaybackProtocol {
  if (explicit) return explicit;
  return /\.m3u8(\?|$)/i.test(url) ? 'hls' : 'whep';
}

/**
 * Whether this element can genuinely play HLS from a plain `src`.
 *
 * **`canPlayType` alone is a trap.** Chrome answers `"maybe"` for
 * `application/vnd.apple.mpegurl` and then plays nothing — setting `src` there
 * produces a permanently black video with no error at all.
 *
 * The reliable signal is Media Source Extensions: where MSE exists, hls.js is
 * the right player (this is hls.js's own documented rule). Native playback is
 * only trusted where MSE is absent, which is iOS Safari — the one place hls.js
 * cannot run and native HLS genuinely works.
 */
function canPlayHlsNatively(video: HTMLVideoElement): boolean {
  const claimsSupport = typeof video.canPlayType === 'function'
    && video.canPlayType('application/vnd.apple.mpegurl') !== '';
  const hasMse = typeof MediaSource !== 'undefined'
    || typeof (globalThis as any).ManagedMediaSource !== 'undefined';
  return claimsSupport && !hasMse;
}

/** HTTP status of a failed hls.js load, when hls.js knows it. */
function hlsErrorStatus(data: any): number {
  return Number(data?.response?.code ?? data?.networkDetails?.status ?? 0) || 0;
}

async function attachHls(
  url: string,
  video: HTMLVideoElement | null | undefined,
  hlsFactory?: () => any,
  failover?: HlsFailover
): Promise<PlaybackHandle> {
  if (!video) throw new Error('HLS playback needs a <video> element.');
  const native = typeof hlsFactory !== 'function' && canPlayHlsNatively(video);
  if (!native && typeof hlsFactory !== 'function') {
    throw new Error(
      'HLS needs an hlsFactory on this browser. Pass () => new Hls(...) from hls.js; '
      + 'it is not bundled with the SDK. Only iOS Safari, which has no Media Source '
      + 'Extensions, plays HLS without one — Chrome reports that it can and then '
      + 'plays nothing.'
    );
  }

  // Without failover options the behaviour is exactly the single-URL one callers
  // already rely on (some install their own hls.js error handling).
  const urls = [url, ...(failover?.fallbackUrls || []).filter((candidate) => candidate !== url)];
  const failoverWanted = Boolean(failover && (failover.fallbackUrls.length || failover.onPlaybackError));
  let index = 0;
  let hls: any = null;
  let detached = false;
  let finished = false;
  let mediaRecovered = false;
  let switching = false;

  const handle: PlaybackHandle = {
    protocol: 'hls',
    url,
    stream: null,
    detach: () => {
      detached = true;
      video.removeEventListener?.('error', onNativeError);
      if (hls) { try { hls.destroy(); } catch { /* already destroyed */ } hls = null; }
      video.removeAttribute('src');
      if (native) video.load?.();
    },
  };

  const giveUp = (message: string) => {
    if (finished || detached) return;
    finished = true;
    failover?.onPlaybackError?.(new Error(message));
  };

  // Prefer the injected player wherever one is available: hls.js works on every
  // browser that supports MSE, including desktop Safari, and gives one code path
  // for live-edge handling rather than two.
  const start = () => {
    mediaRecovered = false;
    if (native) {
      video.src = urls[index];
      const play = video.play?.();
      play?.catch?.(() => {});
      return;
    }
    if (hls) { try { hls.destroy(); } catch { /* already destroyed */ } }
    hls = hlsFactory!();
    if (failoverWanted && typeof hls.on === 'function') {
      // 'hlsError' is Hls.Events.ERROR; hls.js is injected, so its constants are not importable here.
      const current = hls;
      current.on('hlsError', (_event: unknown, data: any) => {
        if (detached || current !== hls || !data?.fatal) return;
        if (data.type === 'mediaError' && !mediaRecovered && typeof current.recoverMediaError === 'function') {
          mediaRecovered = true;
          current.recoverMediaError();
          return;
        }
        const status = hlsErrorStatus(data);
        if (status === 401 || status === 403) { giveUp('The stream is no longer authorized.'); return; }
        advance();
      });
    }
    if (index > 0 && typeof hls.on === 'function') {
      // A viewer who was watching keeps watching: the caller's own play() went to the player that failed.
      // 'hlsManifestParsed' is Hls.Events.MANIFEST_PARSED.
      hls.on('hlsManifestParsed', () => {
        if (detached) return;
        const play = video.play?.();
        play?.catch?.(() => {});
      });
    }
    hls.loadSource(urls[index]);
    hls.attachMedia(video);
  };

  const advance = () => {
    if (detached || finished) return;
    if (index + 1 >= urls.length) { giveUp('The stream could not be played from any of its URLs.'); return; }
    index += 1;
    handle.url = urls[index];
    failover?.onSourceChange?.(urls[index], index);
    start();
  };

  // Native HLS hides the HTTP status, so ask the URL itself before moving on:
  // a refused token stays refused rather than being retried elsewhere.
  async function onNativeError() {
    if (detached || finished || switching) return;
    switching = true;
    let status = 0;
    try {
      const response = await fetch(urls[index], { method: 'GET', cache: 'no-store' });
      status = response.status;
    } catch { /* unreachable: try the next URL */ }
    switching = false;
    if (status === 401 || status === 403) { giveUp('The stream is no longer authorized.'); return; }
    advance();
  }
  if (native && failoverWanted) video.addEventListener?.('error', onNativeError);

  start();
  return handle;
}

/**
 * WHEP: POST an SDP offer, receive an answer, play the tracks.
 *
 * Implemented directly against RTCPeerConnection rather than via a library —
 * WHEP is a small, standard exchange and a dependency here would be larger than
 * the protocol.
 */
async function attachWhep(
  url: string,
  video: HTMLVideoElement | null | undefined,
  headers: Record<string, string> = {},
  iceServers?: RTCIceServer[]
): Promise<PlaybackHandle> {
  if (typeof RTCPeerConnection === 'undefined') {
    throw new Error('WHEP playback needs WebRTC support.');
  }
  const pc = new RTCPeerConnection(iceServers?.length ? { iceServers } : undefined);
  const stream = new MediaStream();

  // recvonly: this is egress. Adding transceivers up front means the offer
  // advertises both kinds even before any track arrives.
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });
  pc.ontrack = (event: RTCTrackEvent) => {
    if (event.track) stream.addTrack(event.track);
    if (video && video.srcObject !== stream) {
      video.srcObject = stream;
      const play = video.play?.();
      play?.catch?.(() => {});
    }
  };

  let resourceUrl = '';
  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp', ...headers },
      body: offer.sdp || '',
    });
    if (!response.ok) {
      throw new Error(`The WHEP endpoint refused the request (${response.status}).`);
    }
    // The Location header names the session, and DELETEing it is the only way
    // to tell the server to stop sending. Without it a closed page keeps a
    // session alive server-side.
    const location = response.headers.get('Location') || '';
    resourceUrl = location ? new URL(location, url).toString() : '';

    const answer = await response.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answer });
  } catch (error) {
    pc.close();
    throw error;
  }

  return {
    protocol: 'whep',
    url,
    stream,
    detach: () => {
      if (resourceUrl) {
        // Best-effort and deliberately unawaited: teardown often runs during
        // page unload, where waiting is not an option.
        try { void fetch(resourceUrl, { method: 'DELETE', headers }); } catch { /* ignore */ }
      }
      try { pc.close(); } catch { /* already closed */ }
      stream.getTracks().forEach((track) => track.stop());
      if (video && video.srcObject === stream) video.srcObject = null;
    },
  };
}

export type AttachPlaybackType = (options: AttachPlaybackOptions) => Promise<PlaybackHandle>;

/**
 * Start an explicit playback.
 *
 * @example
 * ```ts
 * import Hls from 'hls.js';
 *
 * const [source] = getPlaybackSources({ parameters });
 * const handle = await attachPlayback({
 *   source,
 *   video: videoRef.current,
 *   hlsFactory: () => new Hls({ lowLatencyMode: true }),
 * });
 * // later, always:
 * detachPlayback(handle);
 *
 * // A broadcast from the REST API: keep playing from the media server if the CDN fails.
 * const [primary, ...fallbackSources] = getHlsPlaybackCandidates(broadcast);
 * const live = await attachPlayback({
 *   source: primary,
 *   fallbackSources,
 *   video: videoRef.current,
 *   hlsFactory: () => new Hls(),
 *   onPlaybackError: (error) => showMessage(error.message),
 * });
 * ```
 */
export async function attachPlayback({
  source,
  video,
  protocol,
  hlsFactory,
  headers,
  iceServers,
  fallbackSources,
  onSourceChange,
  onPlaybackError,
}: AttachPlaybackOptions): Promise<PlaybackHandle> {
  const url = typeof source === 'string' ? source : source?.url || '';
  if (!url) throw new Error('A playback URL is required.');
  const resolved = typeof source === 'string'
    ? inferProtocol(url, protocol)
    : (protocol || source.protocol || inferProtocol(url));

  if (resolved !== 'hls') return attachWhep(url, video, headers, iceServers);
  const fallbackUrls = (Array.isArray(fallbackSources) ? fallbackSources : [])
    .map((entry) => (typeof entry === 'string' ? entry : entry?.url || ''))
    .filter((candidate) => /^https:\/\//i.test(candidate));
  const failover = fallbackUrls.length || onPlaybackError || onSourceChange
    ? { fallbackUrls, onSourceChange, onPlaybackError }
    : undefined;
  return attachHls(url, video, hlsFactory, failover);
}

/** Stop a playback and release everything it holds. Safe to call twice. */
export function detachPlayback(handle: PlaybackHandle | null | undefined): void {
  if (!handle || typeof handle.detach !== 'function') return;
  try { handle.detach(); } catch { /* teardown must not throw */ }
}

/* ------------------------------------------------------------------ *
 * WHIP — the ingest counterpart
 * ------------------------------------------------------------------ */

export interface PublishWhipOptions {
  /** The WHIP endpoint. */
  url: string;
  /** The stream to publish. */
  stream: MediaStream;
  headers?: Record<string, string>;
  iceServers?: RTCIceServer[];
}

/**
 * Publish a stream to a WHIP endpoint.
 *
 * The symmetric counterpart of WHEP, and equally explicit. This is *not* how
 * you publish into a MediaSFU room — use `produceMedia` for that, which goes
 * through the room's own transport and respects its capture entitlements. WHIP
 * is for pushing to a standards-based ingest that is not this room.
 */
export async function publishWhip({
  url,
  stream,
  headers = {},
  iceServers,
}: PublishWhipOptions): Promise<PlaybackHandle> {
  if (typeof RTCPeerConnection === 'undefined') {
    throw new Error('WHIP publishing needs WebRTC support.');
  }
  if (!stream?.getTracks?.().length) throw new Error('A stream with at least one track is required.');

  const pc = new RTCPeerConnection(iceServers?.length ? { iceServers } : undefined);
  stream.getTracks().forEach((track) => pc.addTrack(track, stream));

  let resourceUrl = '';
  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp', ...headers },
      body: offer.sdp || '',
    });
    if (!response.ok) throw new Error(`The WHIP endpoint refused the request (${response.status}).`);
    const location = response.headers.get('Location') || '';
    resourceUrl = location ? new URL(location, url).toString() : '';
    await pc.setRemoteDescription({ type: 'answer', sdp: await response.text() });
  } catch (error) {
    pc.close();
    throw error;
  }

  return {
    protocol: 'whep',
    url,
    stream,
    detach: () => {
      if (resourceUrl) {
        try { void fetch(resourceUrl, { method: 'DELETE', headers }); } catch { /* ignore */ }
      }
      try { pc.close(); } catch { /* already closed */ }
    },
  };
}
